<template>
  <div
    class="node sitemap-node"
    :class="{
      active,
      hovered,
      compact,
      expandable: node.items.length,
      grouped: isGrouped,
      'virtual-group': virtualGroup
    }"
    :style="{
      left: layout.left + 'px',
      top: layout.top + 'px',
      width: nodeWidth + 'px',
      height: height + 'px'
    }"
  >
    <div
      class="area"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
      @click="onNodeAreaClick"
    >
      <div class="area-header" @click.stop="onNodeHeaderClick">
        <i v-if="node.items.length" class="icon icon--arrow-right-small-black" :class="{expanded: expanded}"></i>
        <i v-if="hasGroupedUrls(node)" class="icon icon--layers"></i>
        <p class="pathname">
          <span
            v-for="(part, index) in labelParts"
            :key="`${node._key}-label-${index}`"
            :class="{ 'path-parameter': part.dynamic }"
          >{{ part.text }}</span>
        </p>
        <span v-if="hasGroupedUrls(node)" class="grouped-url-count">
          {{ getGroupedUrlCount(node) }}
        </span>
        <button
          v-if="!virtualGroup"
          type="button"
          class="open-report"
          @click.stop="$emit('select', node)"
        >
          <i class="icon icon--heatmap-arrow"></i>
        </button>
      </div>
      <div
        v-if="!compact"
        class="thumbnail-overflow"
        :class="{
          'no-thumbnail': !node._thumbnailUrl,
          'report-link': !virtualGroup
        }"
        @click.stop="$emit('select', node)"
      >
        <div
          class="thumbnail"
          :style="{ 'background-image': `url('${thumbnailUrl}')` }"
        />
      </div>
    </div>
    <div
      v-if="shouldShowNodeInfo(node)"
      class="node-info"
      :class="{
        loading: isNodeInspectionLoading(node),
        'loading-overlay': isNodeInspectionLoading(node) && getNodeInspectionInfo(node).available
      }"
    >
      <template v-if="getNodeInspectionInfo(node).available">
        <div class="info-content">
          <div
            class="info-item schema-item"
            v-btf-tooltip:bottom="{ contents: schemaTooltip, padding: '10px 12px' }"
          >
            <span
              class="schema-status"
              :class="{ exists: getNodeInspectionInfo(node).schemaTagExists }"
              role="img"
              :aria-label="schemaTooltip"
            >
              <i class="icon icon--page-tag size-24"></i>
            </span>
          </div>
          <div class="info-divider"></div>
          <div
            class="info-item geo-score-item"
            :style="{ '--geo-score-color': getGeoScoreColor(node) }"
            v-btf-tooltip:bottom="{ contents: geoScoreTooltip, padding: '10px 12px' }"
          >
            <strong
              class="geo-score-ring"
              role="img"
              :aria-label="geoScoreTooltip"
            >
              <svg viewBox="0 0 32 16" aria-hidden="true">
                <path
                  class="ring-track"
                  d="M1 16 A15 15 0 0 1 31 16"
                  pathLength="100"
                ></path>
                <path
                  class="ring-progress"
                  d="M1 16 A15 15 0 0 1 31 16"
                  pathLength="100"
                  :stroke-dasharray="`${getNormalizedGeoScore(node)} 100`"
                ></path>
              </svg>
              <span>{{ getNodeInspectionInfo(node).geoScore }}</span>
            </strong>
            <span class="info-label">GEO</span>
          </div>
        </div>
      </template>
      <div v-if="isNodeInspectionLoading(node)" class="info-loading">
        <span class="loading-spinner"></span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getGeoScoreColor,
  getGroupedUrlCount,
  getNodeInspectionInfo,
  getNormalizedGeoScore,
  getUrlParameterParts,
  hasGroupedUrls,
  isGroupedUrlNode,
  isNodeInspectionLoading,
  shouldShowNodeInfo
} from '@/libs/sitemap'

export default {
  name: 'BaSitemapNodeItem',
  props: {
    node: {
      type: Object,
      required: true
    },
    layout: {
      type: Object,
      required: true
    },
    nodeWidth: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    active: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    defaultThumbnail: {
      type: String,
      default: ''
    },
    virtualGroup: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      hovered: false
    }
  },
  computed: {
    isGrouped () {
      return isGroupedUrlNode(this.node)
    },
    labelParts () {
      return getUrlParameterParts(this.getNodeLabel(this.node))
    },
    thumbnailUrl () {
      return this.node._thumbnailUrl || this.defaultThumbnail
    },
    schemaTooltip () {
      return `${this.$t('observedSitemap.inspection.schemaTag')} ${
        getNodeInspectionInfo(this.node).schemaTagExists
          ? this.$t('observedSitemap.inspection.exists')
          : this.$t('observedSitemap.inspection.notExists')
      }`
    },
    geoScoreTooltip () {
      return `${this.$t('observedSitemap.inspection.geoScore')}`
    }
  },
  methods: {
    getGeoScoreColor,
    getGroupedUrlCount,
    getNodeInspectionInfo,
    getNormalizedGeoScore,
    hasGroupedUrls,
    isNodeInspectionLoading,
    shouldShowNodeInfo,
    getNodeLabel (node) {
      return (node.name || node.pathname || node.path || '').replace(/^https?:\/\//, '')
    },
    onNodeAreaClick () {
      if (this.compact) {
        this.onNodeHeaderClick()
      }
    },
    onNodeHeaderClick () {
      if (this.node.items.length) {
        this.$emit('toggle', this.node)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.node {
  position: absolute;
  cursor: default;
  transform: translate3d(0, 0, 0);
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  transition: left 0.35s ease, top 0.35s ease, transform 0.5s ease;

  &.active,
  &.hovered,
  &:hover {
    z-index: 3;
  }

  &.active:not(.virtual-group) {
    .area {
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 2px #0274ff, 0 0 0 1px rgba(0, 0, 0, 0.05);
    }
  }

  &.expandable {
    &.compact .area,
    .area-header {
      cursor: pointer;
    }
  }

  &.grouped {
    .area-header .pathname {
      flex: 1;
      color: #456b70;
    }
  }

  &.virtual-group {
    .area {
      width: fit-content;
      max-width: 100%;
      margin: -1px auto 0;
      border-radius: 50px;
      padding-right: 20px;
      background-color: #ffffff;
      box-shadow: none;
    }
  }

  &.compact {
    .area {
      height: 26px;
      justify-content: center;
      gap: 0;
    }

    .area-header {
      min-width: 0;

      .grouped-url-count {
        height: 18px;
        padding: 0 5px;
        line-height: 18px;
      }
    }

    .node-info {
      bottom: 0px;
      border-top: 1px solid #dce9eb;
      width: 100%;
    }
  }
}

.area {
  position: relative;
  height: 121px;
  padding: 4px;
  margin: -1px -1px 0;
  border-radius: 8px;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 0 #0274ff, 0 0 0 1px rgba(0, 0, 0, 0.05);
  box-sizing: content-box;
  background-color: #ffffff;
  overflow: hidden;
  z-index: 1;
  transition: box-shadow 0.15s ease-out;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &-header {
    display: flex;
    align-items: center;
    padding-left: 6px;
    min-width: 0;
    gap: 3px;

    .icon {
      flex: 0 0 auto;
      transition: transform 0.3s ease;

      &.expanded {
        transform: rotate(90deg);
      }
    }

    .pathname {
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
      -webkit-user-select: none;
      color: #767676;
      font-weight: 500;

      .path-parameter {
        display: inline-block;
        border-radius: 4px;
        color: #0E90A5;
        font-weight: 600;
        vertical-align: middle;
      }
    }

    .grouped-url-count {
      flex: 0 0 auto;
      max-width: 54px;
      height: 18px;
      padding: 0 6px;
      border-radius: 10px;
      background-color: #eef6f7;
      color: #148092;
      font-size: 10px;
      font-weight: 500;
      line-height: 18px;
      text-align: center;
      overflow: hidden;
      box-sizing: border-box;
    }

    .open-report {
      flex: 0 0 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      margin-left: auto;
      border-radius: 50%;
      cursor: pointer;

      .icon {
        filter: brightness(0.8);
      }

      &:hover {
        background-color: #f0f0ed;
        .icon {
          filter: brightness(0.5);
        }
      }
    }
  }

  .detail-node {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
  }
}

.thumbnail-overflow,
.thumbnail {
  width: 100%;
  height: 100%;
}

.thumbnail-overflow {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 7px;
  background-color: #f4f4f1;

  &.report-link {
    cursor: pointer;
  }

  &.no-thumbnail {
    &::before {
      @include positionXYcenter;
      z-index: 1;
      content: "";
      background: url('../../assets/img/journey/icon_add_image.svg') no-repeat center center;
      width: 50px;
      height: 50px;
      transition: opacity 0.2s ease;
    }

    .thumbnail {
      transform: scale(1.175);
      filter: blur(15px);

      &::after {
        background: rgba(0, 0, 0, 0.15);
      }
    }
  }
}

.thumbnail {
  position: relative;
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 7px;
    background: rgba(0, 0, 0, 0.15);
  }
}

.node-info {
  position: absolute;
  left: 50%;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  width: calc(100% - 30px);
  height: 52px;
  padding: 7px 10px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  transition: box-shadow 0.15s ease-out;
  transform: translateX(-50%);

  .info-item {
    flex: 1 1 0;
    display: grid;
    row-gap: 6px;
    align-items: center;
    justify-items: center;
    min-width: 0;
    text-align: center;
  }

  .info-label {
    display: block;
    margin: 0;
    color: #4b8f0d;
    font-size: 10px;
    line-height: 10px;
    font-weight: 600;
  }

  .geo-score-item {
    display: grid;

    .info-label {
      color: var(--geo-score-color, #4b8f0d);
    }
  }

  .schema-item {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .schema-status {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 2px solid #bfc5c8;
    border-radius: 50%;
    box-sizing: border-box;

    .icon--page-tag {
      width: 17px;
      height: 17px;
      filter: grayscale(1);
      opacity: 0.35;
      transition: filter 0.15s ease, opacity 0.15s ease;
    }

    &.exists {
      border-color: #0071e3;

      .icon--page-tag {
        filter: none;
        opacity: 1;
      }
    }
  }

  .geo-score-ring {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 16px;
    color: var(--geo-score-color, #4b8f0d);
    font-size: 12px;
    font-weight: 600;
    line-height: 1;

    svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    path {
      fill: none;
      stroke-width: 2px;
    }

    .ring-track {
      stroke: rgb(215, 215, 215);
    }

    .ring-progress {
      stroke: var(--geo-score-color, #4b8f0d);
      stroke-linecap: round;
    }

    span {
      position: relative;
      z-index: 1;
      margin-top: 9px;
      font-size: 11px;
      font-weight: 500;
    }
  }

  .info-divider {
    flex: 0 0 1px;
    width: 1px;
    height: 24px;
    margin: 0 8px;
    background-color: #e5e5e1;
  }

  &.loading {
    justify-content: center;
    background-color: #fafaf8;
  }

  &.loading-overlay {
    background-color: #ffffff;

    .info-content {
      opacity: 0.28;
    }
  }

  .info-content {
    display: flex;
    align-items: center;
    width: 100%;
    transition: opacity 0.15s ease-out;
  }

  .info-loading {
    position: absolute;
    inset: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(120, 120, 120, 0.22);
    border-top-color: #8a8a8a;
    border-radius: 50%;
    box-sizing: border-box;
    animation: node-info-spin 0.8s linear infinite;
  }
}

@keyframes node-info-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
